import { useState, useMemo } from 'react'
import { useCopybookStore } from '../store/copybookStore'
import type { ContentItem, ContentCategory, Dynasty } from '../data/contentLibrary'
import {
  DYNASTY_LABELS,
  CATEGORY_LABELS,
  filterItems,
  generateId,
  getDynastiesByCategory,
  getAuthorsByCategoryAndDynasty,
} from '../data/contentLibrary'

interface ContentLibraryProps {
  items: ContentItem[]
  userItems: ContentItem[]
  activeContentId: string | null
  contentSource: ContentCategory | 'custom'
  customText: string
  onPick: (item: ContentItem) => void
  onChangeSource: (source: ContentCategory | 'custom') => void
  onChangeCustomText: (text: string) => void
  onAddItem: (item: ContentItem) => void
  onUpdateItem: (id: string, updates: Partial<ContentItem>) => void
  onDeleteItem: (id: string) => void
}

const CATEGORIES: { key: ContentCategory | 'custom'; label: string }[] = [
  { key: 'custom', label: '自訂輸入' },
  { key: 'poem', label: '詩詞歌賦' },
  { key: 'classic', label: '經典文章' },
  { key: 'sutra', label: '常用抄經' },
  { key: 'calligraphy', label: '經典碑帖' },
  { key: 'control', label: '控筆練習' },
  { key: 'radical', label: '相同部首' },
  { key: 'common', label: '常用字例字' },
  { key: 'lyrics', label: '經典歌詞' },
  { key: 'structure', label: '間架結構32法' },
]

const ALL = '' as const

export default function ContentLibrary({
  items,
  userItems,
  activeContentId,
  contentSource,
  customText,
  onPick,
  onChangeSource,
  onChangeCustomText,
  onAddItem,
  onUpdateItem,
  onDeleteItem,
}: ContentLibraryProps) {
  const allItems = useMemo(() => [...items, ...userItems], [items, userItems])

  const [selCategory, setSelCategory] = useState<ContentCategory | ''>(ALL)
  const [selDynasty, setSelDynasty] = useState<Dynasty | ''>(ALL)
  const [selAuthor, setSelAuthor] = useState<string>(ALL)

  const [showManager, setShowManager] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [isExpandedByUser, setIsExpandedByUser] = useState(false)
  const [isCustomEditorCollapsed, setIsCustomEditorCollapsed] = useState(true)
  const isListCollapsed = !!activeContentId && !isExpandedByUser

  const selectedItem = useMemo(() => {
    return allItems.find((item) => item.id === activeContentId) || null
  }, [allItems, activeContentId])

  const [formCategory, setFormCategory] = useState<ContentCategory>('poem')
  const [formDynasty, setFormDynasty] = useState<Dynasty>('tang')
  const [formTitle, setFormTitle] = useState('')
  const [formAuthor, setFormAuthor] = useState('')
  const [formDescription, setFormDescription] = useState('')
  const [formContent, setFormContent] = useState('')

  const availableDynasties = useMemo(
    () => (selCategory ? getDynastiesByCategory(selCategory) : []),
    [selCategory, items, userItems],
  )

  const availableAuthors = useMemo(() => {
    if (!selCategory || !selDynasty) return []
    const fromBuiltin = getAuthorsByCategoryAndDynasty(selCategory, selDynasty)
    const fromUser = userItems
      .filter((i) => i.category === selCategory && i.dynasty === selDynasty)
      .map((i) => i.source)
    return [...new Set([...fromBuiltin, ...fromUser])]
  }, [selCategory, selDynasty, userItems])

  const filtered = useMemo(
    () => filterItems(allItems, selCategory, selDynasty, selAuthor),
    [allItems, selCategory, selDynasty, selAuthor],
  )

  function resetFilters(category: ContentCategory | '') {
    setSelCategory(category)
    setSelDynasty(ALL)
    setSelAuthor(ALL)
  }

  function handleCategoryTabClick(key: ContentCategory | 'custom') {
    onChangeSource(key)
    setIsExpandedByUser(true)
    if (key === 'custom') {
      setIsCustomEditorCollapsed(false)
    }
    if (key !== 'custom') {
      resetFilters(key)
    }
  }

  function handleEditItem(item: ContentItem) {
    setEditId(item.id)
    setFormCategory(item.category)
    setFormDynasty(item.dynasty)
    setFormTitle(item.title)
    setFormAuthor(item.source)
    setFormDescription(item.description)
    setFormContent(item.content)
    setShowManager(true)
  }

  function handleSaveForm() {
    if (!formTitle.trim() || !formContent.trim() || !formAuthor.trim()) return
    if (editId) {
      onUpdateItem(editId, {
        category: formCategory,
        dynasty: formDynasty,
        title: formTitle,
        source: formAuthor,
        description: formDescription,
        content: formContent,
      })
    } else {
      onAddItem({
        id: generateId(formCategory, formTitle),
        category: formCategory,
        dynasty: formDynasty,
        title: formTitle,
        source: formAuthor,
        description: formDescription,
        content: formContent,
      })
    }
    setShowManager(false)
    setEditId(null)
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-2">
      {/* 類別 tabs */}
      {(!isListCollapsed || contentSource === 'custom') && (
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((tab) => {
            const active = contentSource === tab.key
            return (
              <button
                key={tab.key}
                onClick={() => handleCategoryTabClick(tab.key)}
                className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                  active ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {tab.label}
              </button>
            )
          })}
        </div>
      )}

      {/* 自訂輸入 */}
      {contentSource === 'custom' ? (
        <div className="mt-4">
          <div className="mb-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <label className="block text-sm font-bold text-gray-700">自訂內容</label>
              <span className="text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200/60 px-2 py-0.5 rounded-full font-mono">
                {customText.trim().length} 字
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              {!isCustomEditorCollapsed && (
                <>
                  <label className="flex items-center gap-1 text-[11px] text-gray-600 cursor-pointer select-none mr-1" title="去標點時是否一併過濾換行/跳行">
                    <input
                      type="checkbox"
                      checked={useCopybookStore.getState().filterNewlinesOnClean}
                      onChange={(e) => useCopybookStore.getState().setFilterNewlinesOnClean(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                    />
                    <span>過濾跳行</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      if (useCopybookStore.getState().filterNewlinesOnClean) {
                        const cleaned = customText.replace(/[\s\p{P}\p{S}]/gu, '')
                        onChangeCustomText(cleaned)
                      } else {
                        const cleaned = customText
                          .replace(/[^\S\r\n\u3000]|\p{P}|\p{S}/gu, '')
                          .replace(/\n{3,}/g, '\n\n')
                        onChangeCustomText(cleaned)
                      }
                    }}
                    className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-gray-600 border border-gray-200 hover:bg-gray-50 shadow-sm"
                    title="過濾標點符號（可勾選是否過濾跳行）"
                  >
                    去標點
                  </button>
                  <button
                    type="button"
                    onClick={() => onChangeCustomText('')}
                    className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-rose-600 border border-rose-100 hover:bg-rose-50 shadow-sm"
                    title="清空自訂文字"
                  >
                    清空
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() => setIsCustomEditorCollapsed((v) => !v)}
                className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
                aria-expanded={!isCustomEditorCollapsed}
                aria-controls="custom-content-editor"
              >
                {isCustomEditorCollapsed ? '展開編輯' : '收起'}
              </button>
            </div>
          </div>
          {isCustomEditorCollapsed ? (
            <button
              type="button"
              onClick={() => setIsCustomEditorCollapsed(false)}
              className="w-full rounded-xl border border-gray-200 bg-gray-50 p-3 text-left text-sm text-gray-600 hover:border-gray-300"
            >
              <span className="line-clamp-2 block leading-6 font-medium">
                {customText.trim() || '尚未輸入內容（點擊展開自訂輸入）'}
              </span>
              <span className="mt-1 block text-xs text-gray-400">
                {customText.length} 字，點擊展開自訂輸入
              </span>
            </button>
          ) : (
            <textarea
              id="custom-content-editor"
              value={customText}
              onChange={(e) => onChangeCustomText(e.target.value)}
              placeholder="輸入要做成字帖的內容..."
              className="h-32 w-full rounded-xl border border-gray-300 p-3 text-sm leading-6 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 font-sans"
            />
          )}
        </div>
      ) : isListCollapsed && selectedItem ? (
        <div className="mt-2 rounded-xl border border-blue-100 bg-blue-50/50 p-4 space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <span className="text-[10px] font-semibold text-blue-700 bg-blue-100/70 border border-blue-200/50 px-2 py-0.5 rounded-full uppercase">
                {CATEGORY_LABELS[selectedItem.category]}
              </span>
              <h3 className="mt-1.5 font-bold text-gray-900 text-sm leading-snug">{selectedItem.title}</h3>
              <p className="text-xs text-gray-500 mt-0.5">來源／作者：{selectedItem.source}</p>
            </div>
            <button
              onClick={() => setIsExpandedByUser(true)}
              className="text-xs font-semibold text-blue-600 hover:text-blue-700 bg-white px-3 py-1.5 rounded-lg border border-blue-200 hover:border-blue-300 transition-colors shadow-sm cursor-pointer whitespace-nowrap"
            >
              更換內容 ✎
            </button>
          </div>
          <div className="bg-white rounded-lg p-3 border border-gray-100 text-xs text-gray-600 line-clamp-2 leading-relaxed font-medium">
            {customText}
          </div>
        </div>
      ) : (
        <>
          {/* 下拉選單列 */}
          <div className="mt-4 flex flex-col sm:flex-row sm:flex-wrap gap-3">
            {/* 類別 */}
            <select
              value={selCategory}
              onChange={(e) => resetFilters(e.target.value as ContentCategory | '')}
              className="w-full sm:w-auto rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500"
            >
              <option value="">全部類別</option>
              {(['poem', 'classic', 'sutra', 'calligraphy', 'radical', 'common', 'lyrics', 'structure'] as const).map((k) => (
                <option key={k} value={k}>{CATEGORY_LABELS[k]}</option>
              ))}
            </select>

            {/* 朝代 */}
            <select
              value={selDynasty}
              onChange={(e) => { setSelDynasty(e.target.value as Dynasty | ''); setSelAuthor(ALL) }}
              className="w-full sm:w-auto rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500"
            >
              <option value="">全部朝代</option>
              {availableDynasties.map((d) => (
                <option key={d} value={d}>{DYNASTY_LABELS[d]}</option>
              ))}
            </select>

            {/* 作者 */}
            <select
              value={selAuthor}
              onChange={(e) => setSelAuthor(e.target.value)}
              className="w-full sm:w-auto rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500"
            >
              <option value="">全部作者</option>
              {availableAuthors.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>

            {/* 管理按鈕 */}
            <button
              onClick={() => setShowManager((v) => !v)}
              className={`w-full sm:w-auto sm:ml-auto rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                showManager
                  ? 'bg-blue-600 text-white'
                  : 'border border-gray-300 text-gray-600 hover:bg-gray-100'
              }`}
            >
              {showManager ? '關閉管理' : '管理內容 +'}
            </button>
          </div>

          {/* 管理面板 */}
          {showManager && (
            <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 p-4">
              <h4 className="mb-3 text-sm font-semibold text-blue-800">
                {editId ? '編輯內容' : '新增內容'}
              </h4>
              <div className="grid gap-3 sm:grid-cols-2">
                <select
                  value={formCategory}
                  onChange={(e) => setFormCategory(e.target.value as ContentCategory)}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500"
                >
                  <option value="poem">詩詞歌賦</option>
                  <option value="classic">經典文章</option>
                  <option value="sutra">常用抄經</option>
                  <option value="calligraphy">經典碑帖</option>
                  <option value="radical">相同部首</option>
                  <option value="common">常用字例字</option>
                  <option value="lyrics">經典歌詞</option>
                  <option value="structure">間架結構32法</option>
                </select>
                <select
                  value={formDynasty}
                  onChange={(e) => setFormDynasty(e.target.value as Dynasty)}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500"
                >
                  {(Object.entries(DYNASTY_LABELS) as [Dynasty, string][]).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
                <input
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  placeholder="標題"
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                />
                <input
                  value={formAuthor}
                  onChange={(e) => setFormAuthor(e.target.value)}
                  placeholder="作者／來源"
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                />
                <input
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="簡短描述"
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 sm:col-span-2"
                />
                <textarea
                  value={formContent}
                  onChange={(e) => setFormContent(e.target.value)}
                  placeholder="內容文字"
                  rows={4}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 sm:col-span-2"
                />
              </div>
              <div className="mt-3 flex gap-2">
                <button
                  onClick={handleSaveForm}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                >
                  {editId ? '更新' : '追加加入'}
                </button>
                <button
                  onClick={() => { setShowManager(false); setEditId(null) }}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-600 hover:bg-gray-100"
                >
                  取消
                </button>
              </div>
            </div>
          )}

          {/* 篩選結果數量 */}
          <p className="mt-3 text-xs text-gray-500">
            共 {filtered.length} 筆內容
            {selCategory && ` · ${CATEGORY_LABELS[selCategory]}`}
            {selDynasty && ` · ${DYNASTY_LABELS[selDynasty]}`}
            {selAuthor && ` · ${selAuthor}`}
          </p>

          {/* 內容列表 */}
          <div className="mt-2 flex flex-col gap-1">
            {filtered.map((item) => {
              const active = activeContentId === item.id
              const isUser = userItems.some((u) => u.id === item.id)

              return (
                <div
                  key={item.id}
                  className={`group relative flex flex-col rounded-lg border transition-colors ${
                    active ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between px-4 py-2.5">
                    <button
                      onClick={() => {
                        onPick(item)
                        setIsExpandedByUser(false)
                      }}
                      className="flex flex-1 flex-wrap items-center gap-2 text-left"
                    >
                      <h4 className="font-medium text-gray-900">{item.title}</h4>
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">
                        {item.source}
                      </span>
                      <span className="rounded bg-gray-50 px-1.5 py-0.5 text-xs text-gray-400">
                        {DYNASTY_LABELS[item.dynasty]}
                      </span>
                      {isUser && (
                        <span className="rounded bg-green-50 px-1.5 py-0.5 text-xs text-green-500">
                          自訂
                        </span>
                      )}
                    </button>
                    {isUser && (
                      <div className="ml-2 flex gap-1">
                        <button
                          onClick={() => handleEditItem(item)}
                          className="rounded px-2 py-1 text-xs text-blue-600 hover:bg-blue-50"
                        >
                          編輯
                        </button>
                        <button
                          onClick={() => onDeleteItem(item.id)}
                          className="rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                        >
                          刪除
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          {filtered.length === 0 && (
            <p className="mt-6 text-center text-sm text-gray-400">
              {selCategory || selDynasty || selAuthor
                ? '沒有符合條件的內容，試試修改篩選條件'
                : '內容庫目前沒有資料'}
            </p>
          )}
        </>
      )}
    </div>
  )
}
